import React from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText,
  PlayCircle,
  Globe,
  BookMarked,
  ShieldCheck,
  Scale,
  Podcast,
  Download,
  Lock,
  Mail,
  Star,
  Stethoscope,
  Coins,
  Rocket,
  BookOpen,
  ShoppingCart,
  ArrowUpRight,
  ClipboardCheck,
  Eye,
} from "lucide-react";
import { PageHero, SectionEyebrow } from "@/components/ui-bits";
import { LeadCaptureModal } from "@/components/LeadCaptureModal";
import { BuyMeACoffeeButton } from "@/components/BuyMeACoffeeButton";
import { MembershipCallout } from "@/components/MembershipCallout";
import { useAuth } from "@/context/AuthContext";
import { openDownload, openDownloadAssets } from "@/utils/openDownload";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Each resource maps 1:1 to a backend RESOURCE_DOWNLOAD_MAP slug. When the
// backend returns { download_ready: true, download_assets }, the modal renders
// every asset button. Until then the "coming-soon" flag flips the card into
// waitlist mode and the modal's success view shows a launch-notice promise.
const RESOURCES = [
  {
    slug: "mobile-notary-field-sop",
    kind: "PDF · FREE · NO EMAIL",
    icon: ClipboardCheck,
    image: "/img/resource-ogo-operational-doctrine.png",
    imageAlt:
      "OGO Mobile Notary Field Execution Standard — tactical planning table with a folded map, leather field notebook, and brass compass",
    title: "Mobile Notary Field Execution SOP",
    body:
      "A practical field workflow covering preparation, travel, signing-table execution, quality control, loan signings, estate planning, hospital/bedside appointments, and general notarizations — with Florida-specific compliance notes clearly identified.",
    cta: "View & Download Free",
    landingUrl: "/mobile-notary-field-sop",
    isNew: true,
  },
  {
    slug: "getting-started-notary-business",
    kind: "EBOOK · 42 PAGES",
    icon: Rocket,
    image: "/img/resource-getting-started-notary.png",
    imageAlt:
      "Getting Started as a Notary & Building a Full-Time Notary Business — 42-page digital guide",
    title:
      "Getting Started as a Notary — Building a Full-Time Notary Business",
    body:
      "A 42-page comprehensive guide covering everything a new notary needs to launch and grow a full-time practice: commissioning, statutory duties, signature validation, documentation discipline, and the business fundamentals of turning notary work into sustainable income.",
    cta: "Download Now (Free Ebook)",
    isNew: true,
  },
  {
    slug: "notary-professional-word-search-book",
    kind: "AMAZON · PAPERBACK",
    icon: BookOpen,
    image: "/img/resource-word-search-book.png",
    imageAlt:
      "The Notary Professional Word Search Book — 100 puzzles, 1,000 essential notary terms",
    title:
      "The Notary Professional Word Search Book",
    body:
      "The fun way to master notary knowledge. 100 puzzles and 1,000 essential notary terms — perfect for new, seasoned, and aspiring notaries. Boost confidence, sharpen your expertise, and enjoy brain-boosting puzzles that build real career advantage. Available now on Amazon.",
    cta: "Buy Now on Amazon",
    buyUrl:
      "https://www.amazon.com/Notary-Professional-Word-Search-Book/dp/B0HH2TZ4T9/ref=sr_1_1",
    isOwnProduct: true,
    isNew: true,
  },
  {
    slug: "florida-notary-master-checklist",
    kind: "PDF",
    icon: FileText,
    image: "/img/resource-florida-notary-master-checklist.png",
    imageAlt:
      "Checklist page with gold checkmarks resting on a walnut desk beside a brass notary stamp and fountain pen",
    title: "Florida Notary Master Checklist",
    body:
      "Your visual operational blueprint to guarantee flawless execution at the table and in the car.",
    cta: "Download Now (PDF)",
  },
  {
    slug: "10-steps-to-ron-video",
    kind: "VIDEO + PDF",
    icon: PlayCircle,
    image: "/img/resource-10-steps-to-ron.png",
    imageAlt:
      "Modern laptop displaying an abstract remote-signing interface on a slate-blue desk beside a leather portfolio and brass notary seal",
    title: "10 Steps to RON — Video Guide + Companion Checklist",
    body:
      "A step-by-step visual training guide to navigate Remote Online Notarization technology integration — paired with a printable companion checklist you can keep at the terminal.",
    cta: "Get Both (Video + PDF)",
  },
  {
    slug: "apostille-country-quick-reference",
    kind: "PDF",
    icon: Globe,
    image: "/img/resource-apostille-country-quick-reference.png",
    imageAlt:
      "Brass world globe beside a wax-sealed envelope on a dark leather desk, evoking international document authentication",
    title: "Apostille Country Quick-Reference Guide",
    body:
      "Hague vs. non-Hague destinations, document authentication paths, and jurisdictional requirements.",
    cta: "Download Now (PDF)",
  },
  {
    slug: "loan-signing-document-glossary",
    kind: "PDF",
    icon: BookMarked,
    image: "/img/resource-loan-signing-document-glossary.png",
    imageAlt:
      "Stack of leather-bound legal books with gold spines beside an open ledger and magnifying glass on a mahogany desk",
    title: "Loan Signing Document Glossary",
    body:
      "Plain-English explanations of CD, Note, Deed of Trust, and critical loan package instruments.",
    cta: "Download Now (PDF)",
  },
  {
    slug: "ogo-operational-doctrine",
    kind: "PDF",
    icon: ShieldCheck,
    image: "/img/resource-ogo-operational-doctrine.png",
    imageAlt:
      "Overhead tactical planning view — folded map with routing lines, leather field notebook, brass compass, notary seal, and fountain pen",
    title: "OGO Operational Doctrine — Triple-Phase Session Blueprint",
    body:
      "The three-phase field doctrine every OGO agent operates by: Pre-Session Safeguards for risk elimination, In-Session Execution for full Florida Chapter 117 compliance, and Post-Session Logistics for document integrity and financial closeout.",
    cta: "Download Now (PDF)",
  },
  {
    slug: "florida-9-requirements",
    kind: "PDF",
    icon: Scale,
    image: "/img/resource-florida-9-requirements.png",
    imageAlt:
      "Brass sunburst medallion on navy leather with nine gold accent dots on parchment, symbolizing Florida's nine notary requirements",
    title: "Florida 9 Requirements",
    body:
      "Foundational compliance checklist covering mandatory statutory duties for safe notarial execution in Florida.",
    cta: "Download Now (PDF)",
  },
  {
    slug: "unlocking-business-deductions",
    kind: "EBOOK",
    icon: Coins,
    image: "/img/resource-unlocking-business-deductions.png",
    imageAlt:
      "Leather ledger book open beside a small stack of gold coins, a brass calculator, and an antique key on a walnut desk",
    title: "Unlocking Business Deductions — Turning Everyday Expenses into Tax Savings",
    body:
      "A practical ebook for notaries, signing agents, and solopreneurs on identifying and documenting legitimate business deductions — from mileage and home office to supplies, tech, and training — so you keep more of every dollar you earn.",
    cta: "Download Now (Ebook)",
  },
  {
    slug: "wills-estates-trusts-planning",
    kind: "2-PDF PACK",
    icon: BookMarked,
    image: "/img/resource-wills-estates-trusts.png",
    imageAlt:
      "Heirloom leather-bound book with an embossed quill motif beside a brass wax seal, antique key, and testament envelope tied with gold cord",
    title: "Wills, Estates & Trusts — Planning 101 + Sample Scripts",
    body:
      "A two-part educational pack covering the essentials of estate planning documents — Wills, Living Trusts, Powers of Attorney, and Healthcare Directives — paired with ready-to-use sample scripts notaries can adapt when guiding clients through common signings.",
    cta: "Download Now (2-PDF Pack)",
    companion: {
      slug: "wills-estates-trusts-companion-prepare",
      kind: "COMPANION PDF · NEW",
      title: "Before the Notary Arrives — PREPARE",
      body:
        "The third companion in the series. Where the pack above teaches you to KNOW and SAY, this guide is what clients use to PREPARE — so the signing table is ready the moment you walk in.",
      cta: "Download Companion (PDF)",
    },
  },
];

const PODCAST_RESOURCE = {
  slug: "30-day-mobile-notary-roadmap-podcast",
  kind: "AUDIO",
  icon: Podcast,
  image: "/img/resource-30-day-podcast.png",
  imageAlt:
    "Vintage broadcast microphone on a boom arm above a leather notebook with a 30-day calendar grid, coffee mug, and fountain pen",
  title: "The '30-Day Mobile Notary Roadmap' Podcast",
  body:
    "A limited-run audio series walking new mobile notaries through the exact 30-day launch plan Dr. Byers uses to onboard OGO agents — from commission and E&O to first paid signings.",
  cta: "Listen Now (Audio)",
};

const FEATURED_ELDER_CARE = {
  slug: "elder-care-documents-checklist",
  kind: "PDF",
  icon: Stethoscope,
  image: "/img/resource-elder-care-checklist.png",
  imageAlt:
    "Warm bedside scene with a brass lamp, folded blanket, vintage reading glasses on a leather portfolio, and a small potted plant",
  title: "Essential Elder-Care Documents Checklist",
  body:
    "A print-ready 1-page checklist of the 5 core documents every Florida senior should have notarized before a medical event — Durable POA, Healthcare Surrogate, Living Will, HIPAA Authorization, and Self-Proving Will Affidavit. Includes a decision tree so families know exactly what to do next.",
  cta: "Download Checklist (PDF)",
  featuredEyebrow: "Featured Release · Pillar 5 · Elder Care",
};

const FEATURED_CLINICAL_BLUEPRINT = {
  slug: "clinical-notary-blueprint",
  kind: "PDF + VIDEO",
  icon: ShieldCheck,
  image: "/img/resource-clinical-notary-blueprint.png",
  imageAlt:
    "Stethoscope curled around a leather-bound field manual with a brass notary seal, folded document, and compact flashlight on a slate-blue desk",
  title: "The Clinical Notary Blueprint — Dignified Bedside Protocol",
  body:
    "The professional field playbook for notaries serving hospitals, hospices, and senior communities: a two-phase protocol covering ID & witness prep, capacity red flags, private-room signing scripts, signature-by-mark procedures, and the 7-question UPL Shield. Includes the full pack: PDF, companion video walkthrough, and two field graphics.",
  cta: "Download Full Pack (PDF + Video + Graphics)",
  featuredEyebrow: "For Notary Professionals · Community Preview",
  memberNudge: true,
};

// Enrich a resource with the fields the shared LeadCaptureModal expects.
function toModalShape(r) {
  return {
    ...r,
    resourceLabel: `Resource: ${r.title}`,
    source: "resources_page_gated",
  };
}

export default function ResourcesPage() {
  const [modalResource, setModalResource] = React.useState(null);
  const { isAuthed, authHeader } = useAuth();

  // Dual-path handler: logged-in members skip the modal, hit the community
  // download endpoint (which logs the event under their profile), and open
  // the file directly. Guests continue through the existing lead-capture flow.
  const handleResourceRequest = React.useCallback(
    async (resource) => {
      if (!isAuthed || resource.comingSoon) {
        setModalResource(toModalShape(resource));
        return;
      }
      try {
        const { data } = await axios.post(
          `${API}/community/downloads/log`,
          { slug: resource.slug },
          { headers: authHeader() }
        );
        const assets = Array.isArray(data?.download_assets)
          ? data.download_assets
          : [];
        const primary = data?.download_url;
        if (assets.length > 0) {
          const { openedInNewTab } = openDownloadAssets(assets, {
            onBlockedRest: (blocked) => {
              const first = blocked[0];
              toast.info("Additional file ready", {
                description: `Popup was blocked — click to open ${first.label}.`,
                action: {
                  label: "Open",
                  onClick: () => openDownload(first.url),
                },
                duration: 15000,
              });
            },
          });
          const primaryLabel = assets[0]?.label || resource.title;
          const extras = assets.length - 1;
          toast.success(openedInNewTab ? "Download started!" : "Opening download…", {
            description:
              extras > 0
                ? `${primaryLabel} — plus ${extras} companion file${extras === 1 ? "" : "s"} opening in new tabs.`
                : resource.title,
            id: `download-${resource.slug}`,
          });
        } else if (primary) {
          openDownload(primary);
          toast.success("Download started!", {
            description: resource.title,
            id: `download-${resource.slug}`,
          });
        } else {
          toast.error("That resource isn't ready yet.");
        }
      } catch (err) {
        toast.error(
          err?.response?.data?.detail ||
            "Couldn't start that download. Please try again."
        );
      }
    },
    [isAuthed, authHeader]
  );

  return (
    <>
      <PageHero
        eyebrow="Resources"
        title="Practical guides, checklists, and references."
        lead="Curated, plain-English resources for individuals, families, real estate professionals, and corporate clients. New material is added regularly. Drop your name and email on any card below to receive your guide immediately via download."
      />

      {/* Featured resource — Elder Care Documents Checklist (Pillar 5 spotlight) */}
      <section className="pt-24 sm:pt-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          {/* How-it-works — 3-step explainer so first-time visitors know the flow */}
          <div
            data-testid="resources-how-it-works"
            className="mb-10 sm:mb-12 border border-white/10 bg-[#0A1020] px-6 sm:px-8 py-6"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-4">
              How to grab a resource · 3 quick steps
            </div>
            <ol className="grid sm:grid-cols-3 gap-6">
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 border border-[#D4AF37]/50 text-[#D4AF37] flex items-center justify-center text-xs font-bold shrink-0">
                  1
                </span>
                <p className="text-slate-300 text-sm leading-relaxed">
                  <span className="text-white font-semibold">Click Download</span>{" "}
                  on any resource card below.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 border border-[#D4AF37]/50 text-[#D4AF37] flex items-center justify-center text-xs font-bold shrink-0">
                  2
                </span>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Enter your{" "}
                  <span className="text-white font-semibold">name + email</span>{" "}
                  in the pop-up — one-time only.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 border border-[#D4AF37]/50 text-[#D4AF37] flex items-center justify-center text-xs font-bold shrink-0">
                  3
                </span>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Click the{" "}
                  <span className="text-[#D4AF37] font-semibold">
                    gold download button(s)
                  </span>{" "}
                  — multi-file resources show one button per file.
                </p>
              </li>
            </ol>
            <p className="mt-5 text-slate-400 text-xs leading-relaxed">
              <span className="text-[#D4AF37]">Prefer to skip step 2 forever?</span>{" "}
              Create a free member account (button below) — members get 1-click
              downloads on every current and future resource.
            </p>
          </div>

          {/* Membership call-out — front-and-center above the featured releases */}
          <div className="mb-12">
            <MembershipCallout variant="bar" testid="resources-membership-callout" />
          </div>

          {/* General "Support OGO" catch-all for the Resources page */}
          <div
            data-testid="resources-support-bar"
            className="mb-10 sm:mb-12 border border-[#D4AF37]/25 bg-[#0A1020] px-6 sm:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5"
          >
            <div className="max-w-xl">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">
                Enjoying the free library?
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">
                These guides are built and maintained on nights and weekends. If
                one has helped you, a small tip keeps the next one coming — no
                obligation, ever.
              </p>
            </div>
            <BuyMeACoffeeButton
              variant="solid"
              label="Buy Me a Coffee"
              testid="resources-bmc-btn"
            />
          </div>

          <FeaturedResourceBlock
            resource={FEATURED_ELDER_CARE}
            onRequest={() => handleResourceRequest(FEATURED_ELDER_CARE)}
          />

          <div className="mt-10">
            <FeaturedResourceBlock
              resource={FEATURED_CLINICAL_BLUEPRINT}
              onRequest={() => handleResourceRequest(FEATURED_CLINICAL_BLUEPRINT)}
            />
          </div>
        </div>
      </section>

      {/* 6-card gated content grid */}
      <section className="py-24 sm:py-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {RESOURCES.map((r, i) => (
              <ResourceCard
                key={r.slug}
                resource={r}
                index={i}
                onRequest={(target) => handleResourceRequest(target || r)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Bonus Podcast full-width CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <PodcastBlock
            resource={PODCAST_RESOURCE}
            onRequest={() => setModalResource(toModalShape(PODCAST_RESOURCE))}
          />
        </div>
      </section>

      <LeadCaptureModal
        resource={modalResource}
        onClose={() => setModalResource(null)}
      />
    </>
  );
}

function ResourceKindBadge({ kind }) {
  return (
    <span
      data-testid={`resource-kind-${kind.toLowerCase()}`}
      className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-1"
    >
      {kind}
    </span>
  );
}

function ResourceCard({ resource, index, onRequest }) {
  const Icon = resource.icon;
  const isComingSoon = Boolean(resource.comingSoon);
  const hasImage = Boolean(resource.image);
  const isBuy = Boolean(resource.buyUrl);
  const isLanding = Boolean(resource.landingUrl);
  const ButtonIcon = isLanding
    ? Eye
    : isBuy
    ? ShoppingCart
    : isComingSoon
    ? Mail
    : Download;
  const buttonClass = `w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-bold transition-colors ${
    isComingSoon
      ? "border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]"
      : "bg-[#D4AF37] text-[#142339] hover:bg-[#FDE047]"
  }`;
  return (
    <div
      data-testid={`resource-card-${index}`}
      className={`relative border hover:border-[#D4AF37]/40 transition-colors flex flex-col overflow-hidden ${
        isComingSoon
          ? "border-white/5 bg-[#152238]"
          : "border-white/10 bg-[#1A2B45]"
      }`}
    >
      {hasImage && (
        <div
          data-testid={`resource-card-image-${index}`}
          className="relative aspect-[4/3] overflow-hidden border-b border-white/10 bg-[#0B1120]"
        >
          <img
            src={resource.image}
            alt={resource.imageAlt || resource.title}
            loading="lazy"
            className="w-full h-full object-cover"
          />
          {resource.isNew && (
            <span
              data-testid={`resource-new-badge-${index}`}
              className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.22em] bg-[#D4AF37] text-[#142339] font-bold px-2.5 py-1"
            >
              New Release
            </span>
          )}
        </div>
      )}
      <div className="p-8 flex flex-col flex-1">
        {!hasImage && (
          <div className="flex items-start justify-between mb-6">
            <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
              <Icon className="w-6 h-6" />
            </div>
            <div className="flex flex-col items-end gap-2">
              {isComingSoon && (
                <span
                  data-testid={`resource-coming-soon-${index}`}
                  className="text-[10px] uppercase tracking-[0.22em] text-slate-400 border border-white/15 bg-white/[0.02] px-2 py-1"
                >
                  Coming Soon
                </span>
              )}
              <ResourceKindBadge kind={resource.kind} />
            </div>
          </div>
        )}
        {hasImage && (
          <div className="flex items-center justify-between mb-5">
            <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
              <Icon className="w-5 h-5" />
            </div>
            <ResourceKindBadge kind={resource.kind} />
          </div>
        )}
        <h3 className={`font-serif-display text-xl mb-3 leading-tight ${
          isComingSoon ? "text-slate-200" : "text-white"
        }`}>
          {resource.title}
        </h3>
        <p className="text-slate-300 text-sm leading-relaxed flex-1 mb-8">
          {resource.body}
        </p>
        {isLanding ? (
          <Link
            to={resource.landingUrl}
            data-testid={`resource-cta-${index}`}
            className={buttonClass}
          >
            <ButtonIcon className="w-3.5 h-3.5" />
            {resource.cta}
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        ) : isBuy ? (
          <>
            <a
              href={resource.buyUrl}
              target="_blank"
              rel={resource.isOwnProduct ? "noopener noreferrer" : "sponsored noopener noreferrer"}
              data-testid={`resource-cta-${index}`}
              className={buttonClass}
            >
              <ButtonIcon className="w-3.5 h-3.5" />
              {resource.cta}
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
            {!resource.isOwnProduct && (
              <span className="mt-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 text-center">
                #CommissionsEarned · Amazon Associate
              </span>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={() => onRequest()}
            data-testid={`resource-cta-${index}`}
            className={buttonClass}
          >
            <ButtonIcon className="w-3.5 h-3.5" />
            {resource.cta}
          </button>
        )}
        {resource.companion && (
          <div
            data-testid={`resource-companion-${index}`}
            className="mt-6 pt-6 border-t border-dashed border-[#D4AF37]/30"
          >
            <div className="flex items-center gap-2 mb-3">
              <span
                data-testid={`resource-companion-badge-${index}`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] bg-[#D4AF37] text-[#142339] font-bold px-2 py-0.5"
              >
                <Star className="w-3 h-3 fill-[#142339]" />
                New Companion
              </span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]/80">
                Separate Download
              </span>
            </div>
            <h4 className="font-serif-display text-base text-white leading-snug mb-2">
              {resource.companion.title}
            </h4>
            <p className="text-slate-300 text-xs leading-relaxed mb-4">
              {resource.companion.body}
            </p>
            <button
              type="button"
              onClick={() => onRequest(resource.companion)}
              data-testid={`resource-companion-cta-${index}`}
              className="w-full inline-flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#FDE047] hover:text-[#FDE047] px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              {resource.companion.cta}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FeaturedResourceBlock({ resource, onRequest }) {
  const Icon = resource.icon;
  return (
    <div
      data-testid="resource-featured-block"
      className="relative overflow-hidden border border-[#D4AF37]/40 bg-gradient-to-br from-[#1A2B45] via-[#1A2B45] to-[#0F1B2E] p-10 lg:p-14"
    >
      <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#D4AF37]/[0.05] blur-3xl pointer-events-none" />
      <div className="absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-[#D4AF37]/[0.04] blur-3xl pointer-events-none" />

      <div className="relative grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-3 flex lg:justify-center">
          {resource.image ? (
            <div
              data-testid="resource-featured-image"
              className="w-full max-w-[220px] lg:max-w-none aspect-[4/3] overflow-hidden border border-[#D4AF37]/40 bg-[#0B1120]"
            >
              <img
                src={resource.image}
                alt={resource.imageAlt || resource.title}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-20 h-20 border-2 border-[#D4AF37] rounded-full flex items-center justify-center text-[#D4AF37]">
              <Icon className="w-9 h-9" />
            </div>
          )}
        </div>
        <div className="lg:col-span-6">
          <SectionEyebrow className="mb-3 text-[#D4AF37]">
            <span className="inline-flex items-center gap-1.5">
              <Star className="w-3 h-3 fill-[#D4AF37] text-[#D4AF37]" />
              {resource.featuredEyebrow || "Featured Release · Pillar 5 · Elder Care"}
              <Star className="w-3 h-3 fill-[#D4AF37] text-[#D4AF37]" />
            </span>
          </SectionEyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-white leading-tight">
            {resource.title}
          </h2>
          <p className="mt-4 text-slate-300 leading-relaxed">
            {resource.body}
          </p>
        </div>
        <div className="lg:col-span-3 lg:justify-self-end w-full">
          <button
            type="button"
            onClick={onRequest}
            data-testid="resource-featured-cta"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] px-6 py-4 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#FDE047] transition-colors"
          >
            <Download className="w-4 h-4" />
            {resource.cta}
          </button>
          <div className="mt-3 flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-400">
            <Lock className="w-3 h-3 text-[#D4AF37]/70" />
            Gated · Name + Email
          </div>
        </div>
      </div>
    </div>
  );
}

function PodcastBlock({ resource, onRequest }) {
  const Icon = resource.icon;
  return (
    <div
      data-testid="resource-podcast-block"
      className="relative overflow-hidden border border-[#D4AF37]/30 bg-gradient-to-br from-[#1A2B45] via-[#1A2B45] to-[#142339] p-10 lg:p-14"
    >
      <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#D4AF37]/[0.04] pointer-events-none" />
      <div className="absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-[#D4AF37]/[0.03] pointer-events-none" />

      <div className="relative grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-3 flex lg:justify-center">
          {resource.image ? (
            <div
              data-testid="resource-podcast-image"
              className="w-full max-w-[220px] lg:max-w-none aspect-[4/3] overflow-hidden border border-[#D4AF37]/40 bg-[#0B1120]"
            >
              <img
                src={resource.image}
                alt={resource.imageAlt || resource.title}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-20 h-20 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37]">
              <Icon className="w-9 h-9" />
            </div>
          )}
        </div>
        <div className="lg:col-span-6">
          <SectionEyebrow className="mb-3 text-[#D4AF37]">
            <span className="inline-flex items-center gap-1.5">
              <Star className="w-3 h-3 fill-[#D4AF37] text-[#D4AF37]" />
              The Gold Card · Bonus Audio Resource for Notaries
              <Star className="w-3 h-3 fill-[#D4AF37] text-[#D4AF37]" />
            </span>
          </SectionEyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-white leading-tight">
            {resource.title}
          </h2>
          <p className="mt-4 text-slate-300 leading-relaxed">
            {resource.body}
          </p>
        </div>
        <div className="lg:col-span-3 lg:justify-self-end w-full">
          <button
            type="button"
            onClick={onRequest}
            data-testid="resource-podcast-cta"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] px-6 py-4 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#FDE047] transition-colors"
          >
            <PlayCircle className="w-4 h-4" />
            {resource.cta}
          </button>
          <div className="mt-3 flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-400">
            <Lock className="w-3 h-3 text-[#D4AF37]/70" />
            Gated · Name + Email
          </div>
        </div>
      </div>
    </div>
  );
}
